import React, { useMemo } from "react";
import { Layout, Menu, Typography } from "antd";
import type { MenuProps } from "antd";
import { useMenu, useNavigation } from "@refinedev/core";
import type { ThemeMode } from "../../theme";

type MenuItem = Required<MenuProps>["items"][number];

type AdminSiderProps = {
  themeMode: ThemeMode;
};

export const AdminSider: React.FC<AdminSiderProps> = ({ themeMode }) => {
  const { menuItems, selectedKey, defaultOpenKeys } = useMenu();
  const { push } = useNavigation();

  const { items, routeMap } = useMemo(() => {
    const routeStore = new Map<string, string | undefined>();

    const convertItems = (entries: typeof menuItems): MenuItem[] => {
      return entries.map((entry) => {
        routeStore.set(entry.key, entry.route);

        return {
          key: entry.key,
          label: entry.label,
          icon: entry.icon,
          children:
            entry.children.length > 0 ? convertItems(entry.children) : undefined,
        };
      });
    };

    return { items: convertItems(menuItems), routeMap: routeStore };
  }, [menuItems]);

  const handleMenuClick: MenuProps["onClick"] = ({ key }) => {
    const target = routeMap.get(key);
    if (target) {
      push(target);
    }
  };

  const isDark = themeMode === "dark";
  const baseBg = isDark ? "#061024" : "#ffffff";
  const accentBg = isDark ? "#0e1f3f" : "#1890ff";
  const textColor = isDark ? "#cad6ff" : "#102a57";
  const descColor = isDark
    ? "rgba(202,214,255,0.75)"
    : "rgba(255,255,255,0.85)";
  const titleColor = isDark ? "#f4f7ff" : "#ffffff";

  // 浅色模式下菜单项的增强样式
  const menuItemSelectedBg = isDark ? "rgba(24, 144, 255, 0.15)" : "#e6f4ff";
  const menuItemHoverBg = isDark ? "rgba(255, 255, 255, 0.08)" : "#d6e9ff"; // 更深的蓝色
  const menuItemSelectedColor = isDark ? "#91caff" : "#0958d9";
  const menuItemSelectedBorderColor = isDark ? "#1677ff" : "#1677ff";
  const menuItemHoverColor = isDark ? "#cad6ff" : "#003eb3"; // 悬停时文字颜色

  return (
    <>
      {/* 动态菜单样式 + 主内容布局 */}
      <style>{`
        /* 主内容区域左边距，防止被固定菜单遮挡 */
        .ant-layout-has-sider {
          padding-left: 0 !important;
        }
        .ant-layout.ant-layout-has-sider > .ant-layout,
        .ant-layout.ant-layout-has-sider > .ant-layout-content {
          margin-left: 248px;
        }

        /* 菜单项样式 */
        .admin-sidebar-menu .ant-menu-item {
          color: ${textColor};
          border-radius: 8px;
          margin: 4px 12px;
          width: auto;
          transition: all 0.2s ease;
        }
        .admin-sidebar-menu .ant-menu-item:hover {
          background-color: ${menuItemHoverBg} !important;
          color: ${menuItemHoverColor} !important;
        }
        .admin-sidebar-menu .ant-menu-item:hover .ant-menu-item-icon {
          color: ${menuItemHoverColor} !important;
        }
        .admin-sidebar-menu .ant-menu-item-selected {
          background-color: ${menuItemSelectedBg} !important;
          color: ${menuItemSelectedColor} !important;
          font-weight: 600;
          border-left: 3px solid ${menuItemSelectedBorderColor};
        }
        .admin-sidebar-menu .ant-menu-item-selected .ant-menu-item-icon {
          color: ${menuItemSelectedColor} !important;
        }
      `}</style>

      <Layout.Sider
        width={248}
        collapsed={false}
        theme={isDark ? "dark" : "light"}
        style={{
          position: "fixed",
          left: 0,
          top: 0,
          bottom: 0,
          height: "100vh",
          overflow: "auto",
          background: baseBg,
          borderRight: isDark ? "none" : "1px solid rgba(15,23,42,0.08)",
          boxShadow: isDark
            ? "none"
            : "4px 0 12px rgba(15,23,42,0.08)",
          zIndex: 100,
        }}
      >
      <div
        style={{
          padding: "24px 20px 12px",
          background: accentBg,
          color: "#ffffff",
        }}
      >
        <Typography.Title
          level={4}
          style={{
            color: titleColor,
            marginBottom: 4,
          }}
        >
          夜光宝盒管理
        </Typography.Title>
        <Typography.Text
          style={{
            color: descColor,
          }}
        >
          让影片与排期一目了然
        </Typography.Text>
      </div>

      <Menu
        mode="inline"
        selectedKeys={selectedKey ? [selectedKey] : []}
        defaultOpenKeys={defaultOpenKeys}
        items={items}
        onClick={handleMenuClick}
        theme={isDark ? "dark" : "light"}
        style={{
          borderInlineEnd: "none",
          background: "transparent",
        }}
        inlineIndent={24}
        className="admin-sidebar-menu"
      />
      </Layout.Sider>
    </>
  );
};
